import { format, isMatch, parse } from 'date-fns'
import { SearchIcon } from 'lucide-react'
import { useMemo } from 'react'

import { FixSessionMessageDto } from '@/gen/publicApi'

import { Badge } from '@/components/badge'
import CopyToClipboard from '@/components/copyIcon'
import IconButton from '@/components/iconButton'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/sheet'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/tooltip'

import { FixMessagePartDetails, getMessagePartDetails, isAdmin, splitMessageIntoParts } from '@/utils/fix'

const timestampFormats = ['yyyyMMdd-HH:mm:ss', 'yyyyMMdd-HH:mm:ss.SSS', 'yyyyMMdd-HH:mm:ss.SSSSSS']
const formattedDetail = (part: FixMessagePartDetails) => {
    switch (part.format) {
        case 'timestamp':
            for (const timestampFormat of timestampFormats) {
                if (isMatch(part.value, timestampFormat)) {
                    const parsedString = parse(part.value, timestampFormat, new Date())
                    return format(parsedString, 'dd/LL/yyyy HH:mm:ss.SSS')
                }
            }
            // No match, return the original value
            return part.value
        default:
            return part.value
    }
}

interface MessageTableProps {
    messages: FixSessionMessageDto[]
}

export default function RawMessageTable({ messages }: MessageTableProps) {
    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>Direction</TableHead>
                    <TableHead>MsgType</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead />
                    <TableHead />
                </TableRow>
            </TableHeader>
            <TableBody>
                {messages.map(message => (
                    <RawMessageTableRow key={message.id} message={message} />
                ))}
            </TableBody>
        </Table>
    )
}

interface RawMessageTableRowProps {
    message: FixSessionMessageDto
}

function RawMessageTableRow({ message }: RawMessageTableRowProps) {
    const messageParts = useMemo(() => splitMessageIntoParts(message.message), [message.message])
    const messageType = messageParts.find(part => part.tag == 35)
    const messageTypeDetails = getMessagePartDetails(messageType)
    const isAdminMsg = isAdmin(messageType?.value)

    return (
        <Sheet>
            <TableRow>
                <TableCell className="w-8">
                    <Badge variant={message.direction == 'In' ? 'secondary' : 'outline'}>{message.direction}</Badge>
                </TableCell>
                <TableCell className="w-8">
                    {messageTypeDetails?.description && (
                        <Badge variant={isAdminMsg ? 'outline' : 'default'}>{messageTypeDetails?.description}</Badge>
                    )}
                </TableCell>
                <TableCell className="overflow-hidden">
                    <p className="text-ellipsis break-all">{message.message.replace(/\x01/g, ' | ')}</p>
                </TableCell>
                <TableCell className="w-6">
                    <Tooltip>
                        <TooltipTrigger>
                            <SheetTrigger asChild>
                                <IconButton icon={SearchIcon} tooltip="View parsed message details" />
                            </SheetTrigger>
                        </TooltipTrigger>
                        <TooltipContent>View parsed message details</TooltipContent>
                    </Tooltip>
                </TableCell>
                <TableCell className="w-6">
                    <Tooltip>
                        <TooltipTrigger>
                            <CopyToClipboard
                                value={message.message}
                                name="message"
                                tooltip="Copy message to clipboard"
                            />
                        </TooltipTrigger>
                        <TooltipContent>Copy message to clipboard</TooltipContent>
                    </Tooltip>
                </TableCell>
            </TableRow>
            <SheetContent>
                <SheetHeader className="mb-4">
                    <SheetTitle>Message Details</SheetTitle>
                </SheetHeader>
                {messageParts.map(part => {
                    const detail = getMessagePartDetails(part)
                    if (!detail) {
                        return
                    }

                    return (
                        <div key={detail.label} className="mb-2">
                            <h4>{detail.label}</h4>
                            <div className="flex justify-between">
                                <h5>{formattedDetail(detail)}</h5>
                                {detail.description && <Badge>{detail.description}</Badge>}
                            </div>
                        </div>
                    )
                })}
            </SheetContent>
        </Sheet>
    )
}
